import { http } from '@/lib/http'
import { listOrEmpty } from '@/lib/utils'
import type { Property, PropertyAttribute, PropertyCreateInput, PropertyListResponse, PropertyStats, PropertyType, PropertyUpdateInput, PublicVisibility } from '@/types/models'

export interface PropertyListParams {
  page?: number
  size?: number
  /** Case-insensitive match on the title only. */
  searchTerm?: string
  sortBy?: string[]
  sortOrder?: (1 | -1)[]
  /** Only vacant, non-archived properties (pickers). */
  dropdown?: boolean
  /** ISO2: properties whose address is in that country (LP-491). Absent = every country. */
  country?: string
}

/** The mobile list request: everything, archived last then by title. */
export const ALL_PROPERTIES: PropertyListParams = { page: 1, size: 250, sortBy: ['archived', 'title'], sortOrder: [1, 1] }

export const propertyApi = {
  /** GET /properties — Gin binds repeated params (`sortBy=a&sortBy=b`), not the bracket form. */
  list: (params: PropertyListParams = ALL_PROPERTIES) =>
    http.get<PropertyListResponse>('/properties', { params, paramsSerializer: { indexes: null } }),
  /** GET /property/:id — 404 "property not found" when missing, deleted or another landlord's (LP-590). */
  get: (id: string) => http.get<Property>(`/property/${id}`),
  /** POST /property → { propertyId }. 409 on an identical property. */
  create: (body: PropertyCreateInput) => http.post<{ propertyId: string }>('/property', body),
  /** PUT /property/:id → { propertyId }. Send only what changed. */
  update: (id: string, body: PropertyUpdateInput) => http.put<{ propertyId: string }>(`/property/${id}`, body),
  /** DELETE /property/:id — soft delete; the backend does not check for an active lease, the client does. */
  remove: (id: string) => http.delete<string>(`/property/${id}`),
  /** PUT /property/:id/visibility — `request` (needs a paid plan; auto-approves today) or `withdraw`. */
  setVisibility: (id: string, action: 'request' | 'withdraw') => http.put<PublicVisibility>(`/property/${id}/visibility`, { action }),
  attributes: (propertyType: PropertyType): Promise<PropertyAttribute[]> =>
    http.get<PropertyAttribute[]>('/property-attributes', { params: { propertyType } }).then(listOrEmpty),
  stats: (propertyId: string) => http.get<PropertyStats>('/property-stats', { params: { propertyId } }),
}
